// M004-F0634 §0a — the Billing tab: the ONE list.
//
// Default view "Needs approval"; filters Needs approval · Approved ·
// Billed · Problems map one-to-one to the API's views need|ok|billed|prob
// (§0a.1). ONE read per view (GET /api/v1/orgs/:orgId/billing/items, Core
// §5d.4.1); every read returns ALL FOUR counts, so the filter badges come
// from the one call. Rows are GROUPED BY PERSON with server-computed
// subtotals, and the row face carries ONLY admin words: booking title ·
// room · server-rendered date/time · amount · price basis · status_text —
// no internal ids, no dispatch codes, no raw ISO timestamps (§0a.1).
//
// BULK approve (§0a.1): select rows (or approve-all within a group) and
// approve the batch in one POST — ONE step-up challenge per batch; the
// SAME body replays after it. Per-item outcomes render the server's
// message verbatim; a failure on one item never aborts the others.
//
// Row click opens the side drawer (BillingDrawer — no modal, no wizard).
// The tab honours the /app/ledgerApprovals redirect's carry:
// ?posting=<posting id> (the item id) opens that item's drawer once the
// view has loaded. The legacy ?occurrence= parameter is matched against
// the item id too — the §5d.4.1 wire carries no occurrence id, so a
// notification that names only an occurrence id can no longer be resolved
// (Core follow-up; see the MR description).
//
// Permission honesty (§5): a real 403 from the billing read renders
// "You do not have permission to review billing." — nothing else (a failed
// permissions READ is the hub's "Could not load your permissions", never
// this text).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TxFinder from '../TxFinder';
import { billingApi } from '../../services/api';
import BillingDrawer from './BillingDrawer';
import TxChip from '../TxChip';
import BillingStageLines from './BillingStageLines';
import StepUpDialog from '../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import {
  BILLING_VIEWS,
  BILLPIPE_FORBIDDEN_NOTICE,
  DEFAULT_VIEW,
  GATE_CLOSED_BANNER,
  VIEW_LABELS,
  batchOutcomeErrors,
  formatGroupSubtotal,
  formatItemWhen,
  formatMinorAmount,
  isBareStepUpRequired,
  isSettledItem,
  billingStageView,
  normalizeItemsRead,
  viewEmptyState,
  viewFromParam,
} from './billingPipelineModel';

export const BILLPIPE_TAB_TESTID = 'bookings-hub-tabpanel-billing';

const emptyReadState = () => ({
  counts: { need: 0, ok: 0, billed: 0, prob: 0 },
  onHold: false,
  groups: [],
  // The view `groups`/`error` were read for (null before any read). A tab switch changes `view` BEFORE the effect below runs, so for
  // that first commit the state still belongs to the previous view: it is derived as stale (Loading), never shown (see M004-F0634).
  readView: null,
  loading: true,
  error: null,
  forbidden: false,
});

const ItemRow = ({
  organisationId,
  item,
  personName,
  selectable,
  selected,
  onToggle,
  onOpen,
}) => {
  const stageView = billingStageView(item);
  return (
  <div className="billpipe-row-line">
    {selectable ? (
      <input
        type="checkbox"
        className="billpipe-row-select"
        data-testid={`billing-row-select-${item.id}`}
        aria-label={`Select ${item.booking_title} for ${personName}`}
        checked={selected}
        onChange={() => onToggle(item)}
        disabled={!item.can?.approve}
      />
    ) : null}
    <button
      type="button"
      className="billpipe-row"
      data-testid={`billing-row-${item.id}`}
      onClick={() => onOpen(item, personName)}
    >
      <span className="billpipe-row-title">{item.booking_title}</span>
      <span className="billpipe-row-room">{item.room}</span>
      <span className="billpipe-row-when">{formatItemWhen(item)}</span>
      {/* M004-F0676: a settled row states no amount and no price basis. */}
      {isSettledItem(item) ? null : (
        <>
          <span className="billpipe-row-amount">
            {formatMinorAmount(item.amount_minor, item.currency)}
          </span>
          <span className="billpipe-row-basis">{item.price_basis}</span>
        </>
      )}
      {/* M004-F0716 phase B (5.2 row 18): the badge is the STAGE's sentence; Core's status_text only for an older Core with no stage. */}
      <span className={`billpipe-badge billpipe-row-badge${stageView.problem ? ' billpipe-badge-problem' : ''}`} data-testid="billing-row-status" data-stage={stageView.stage || undefined}>
        {stageView.text}
      </span>
      <BillingStageLines view={stageView} testidPrefix="billing-row" />
      {item.problem ? (
        <span className="billpipe-row-problem" data-testid="billing-row-problem">
          {item.problem.text}
        </span>
      ) : null}
    </button>
    {/* M004-F0712 S1: the chip is a SIBLING of the row button (a button inside a button is invalid and a click would do two things).
        Rendered only when Core's billing item carries hand_off_id (non-null from capture once Core need N1 ships); never a placeholder. */}
    <TxChip handOffId={item.hand_off_id} txId={item.tx_id} orgId={organisationId} customerUserId={item.user_id} />
  </div>
  );
};

const BillingTab = ({ organisationId }) => {
  const [view, setView] = useState(() => DEFAULT_VIEW);
  const [state, setState] = useState(emptyReadState);
  const [selected, setSelected] = useState(() => new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchErrors, setBatchErrors] = useState([]);
  const [batchNotice, setBatchNotice] = useState('');
  const [drawerItem, setDrawerItem] = useState(null);
  const [drawerPerson, setDrawerPerson] = useState('');
  const [stepUpChallenge, setStepUpChallenge] = useState(null);
  const pendingReplayRef = useRef(null);
  const viewRef = useRef(view);
  // First commit after a tab switch: `view` is new, `state` is still the previous view's read. Show Loading, never its rows, its empty
  // state or its error.
  const stale = state.readView !== view;
  const groups = stale ? [] : state.groups;
  const loading = state.loading || stale;
  const error = stale ? null : state.error;
  viewRef.current = view;

  const fetchView = useCallback(async (nextView) => {
    if (!organisationId) return null;
    setState((current) => ({ ...current, loading: true, error: null }));
    try {
      const response = await billingApi.getItems(organisationId, nextView);
      const read = normalizeItemsRead(response?.data);
      setState({ ...read, readView: nextView, loading: false, error: null, forbidden: false });
      return read;
    } catch (error) {
      const forbidden = error?.response?.status === 403;
      setState((current) => ({
        ...current,
        readView: nextView,
        groups: current.readView === nextView ? current.groups : [],
        loading: false,
        forbidden,
        error: forbidden
          ? null
          : error?.response?.data?.message || error?.message || 'Could not load billing items.',
      }));
      return null;
    }
  }, [organisationId]);

  useEffect(() => {
    setDrawerItem(null);
    setSelected(new Set());
    setBatchErrors([]);
    setBatchNotice('');
    // The previous view's rows must not stay on screen under the new tab while its read is in flight (they are another view's
    // items: wrong rows under the wrong tab, and selectable for a batch approve). The counts stay; the list shows Loading.
    setState((current) => ({ ...current, groups: [] }));
    fetchView(view);
    // The view switch refetches; a ref keeps fetchView's own identity out
    // of the dependency list so an org change re-runs this effect too.
  }, [organisationId, view]);

  const changeView = (next) => {
    if (batchBusy) return;
    setView(next);
  };

  const refreshView = useCallback(() => fetchView(viewRef.current), [fetchView]);

  // §0a.1 — deep links: ?posting=<item id> (the redirect's carry) opens
  // that item's drawer once the view has loaded; ?occurrence= is matched
  // against the item id as a best effort (the §5d.4.1 wire has no
  // occurrence id). Parameters never auto-clear (idempotent re-open).
  const [searchParams] = useSearchParams();
  const deepLinkAppliedRef = useRef('');
  useEffect(() => {
    const postingId = searchParams.get('posting');
    const occurrenceId = searchParams.get('occurrence');
    if (!postingId && !occurrenceId) return;
    if (loading) return;
    const key = `${organisationId}|${view}|${postingId || ''}|${occurrenceId || ''}`;
    if (deepLinkAppliedRef.current === key) return;
    const wanted = String(postingId || occurrenceId || '');
    for (const group of groups) {
      const match = (group.items || []).find((item) => String(item.id) === wanted);
      if (match) {
        deepLinkAppliedRef.current = key;
        setDrawerItem(match);
        setDrawerPerson(group.person);
        return;
      }
    }
  }, [searchParams, state, organisationId, view]);

  const toggleRow = (item) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  };

  const selectGroup = (group) => {
    setSelected((current) => {
      const next = new Set(current);
      (group.items || []).forEach((item) => {
        if (item.can?.approve) next.add(item.id);
      });
      return next;
    });
  };

  // §0a.1 — the ONE batch action. The body is built ONCE (id + the version
  // read with each row) and replayed unchanged after a step-up.
  const buildBatchBody = (ids) => {
    const items = [];
    for (const group of groups) {
      for (const item of group.items || []) {
        if (ids.includes(item.id)) items.push({ id: item.id, version: item.version });
      }
    }
    return items;
  };

  const runBatch = (ids) => {
    if (!ids.length || batchBusy) return;
    const body = buildBatchBody(ids);
    setBatchBusy(true);
    setBatchErrors([]);
    setBatchNotice('');
    const thunk = () => billingApi.approveItems(organisationId, body);
    thunk()
      .then(async (response) => {
        pendingReplayRef.current = null;
        const errors = batchOutcomeErrors(response?.data?.results);
        const approvedCount = (response?.data?.results || []).filter(
          (outcome) => outcome?.outcome === 'approved',
        ).length;
        setBatchErrors(errors);
        setBatchNotice(approvedCount > 0 ? `Approved ${approvedCount}.` : '');
        setSelected(new Set());
        await refreshView();
      })
      .catch((error) => {
        const challenge = getStepUpChallengeFromError(error);
        if (challenge?.id) {
          // The challenge-bearing arm: the SAME body replays unchanged.
          pendingReplayRef.current = () => runBatch(ids);
          setStepUpChallenge(challenge);
          return;
        }
        if (isBareStepUpRequired(error)) {
          // The §5d.4.1 surface as merged carries a BARE {code,message}
          // 401 — no challenge to satisfy. The server's message renders
          // verbatim; nothing was decided (the whole batch is refused
          // before any item).
          setBatchErrors([{
            id: '',
            message: error?.response?.data?.message || 'A fresh second factor is required.',
            code: 'STEP_UP_REQUIRED',
          }]);
          return;
        }
        setBatchErrors([{
          id: '',
          message: error?.response?.data?.message || error?.message || 'The batch could not be approved.',
          code: error?.response?.data?.code || '',
        }]);
      })
      .finally(() => setBatchBusy(false));
  };

  // The drawer's refetch: re-read the current view and hand back the fresh
  // item (or null when it left the view — approved, rejected, no longer a
  // problem).
  const refetchDrawerItem = useCallback(async () => {
    const read = await refreshView();
    if (!read || !drawerItem) return null;
    for (const group of read.groups) {
      const match = (group.items || []).find((item) => String(item.id) === String(drawerItem.id));
      if (match) return match;
    }
    return null;
  }, [refreshView, drawerItem]);

  const openDrawer = (item, personName) => {
    setDrawerItem(item);
    setDrawerPerson(personName || '');
  };

  const selectedItems = buildBatchBody([...selected]);
  const showBatchBar = view === 'need' && selectedItems.length > 0;

  if (!organisationId) {
    return (
      <div className="billpipe-tab" data-testid={BILLPIPE_TAB_TESTID}>
        <p className="bkh-empty">Select an organisation to see its billing.</p>
      </div>
    );
  }

  return (
    <div className="billpipe-tab" data-testid={BILLPIPE_TAB_TESTID}>
      {/* M004-F0714: search the WHOLE organisation by TX id; it never filters the list below. */}
      <TxFinder orgId={organisationId} />
      {state.forbidden ? (
        <p className="billpipe-error" role="alert" data-testid="billing-permission-denied">
          {BILLPIPE_FORBIDDEN_NOTICE}
        </p>
      ) : null}

      {state.onHold ? (
        <p className="billpipe-gate-banner" data-testid="billing-gate-banner">{GATE_CLOSED_BANNER}</p>
      ) : null}

      {batchNotice ? (
        <p className="billpipe-batch-notice" data-testid="billing-batch-notice">{batchNotice}</p>
      ) : null}
      {batchErrors.length > 0 ? (
        <div className="billpipe-batch-errors" data-testid="billing-batch-errors">
          {batchErrors.map((outcome, index) => (
            <p key={`${outcome.id}-${index}`} className="billpipe-error" role="alert">
              {outcome.id ? `${outcome.id}: ${outcome.message}` : outcome.message}
            </p>
          ))}
        </div>
      ) : null}

      <div className="billpipe-views" role="tablist" aria-label="Billing views">
        {BILLING_VIEWS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={view === candidate}
            className={`bkh-tab${view === candidate ? ' active' : ''}`}
            data-testid={`billing-view-${candidate}`}
            onClick={() => changeView(candidate)}
          >
            {VIEW_LABELS[candidate]}
            {` (${state.counts[candidate] ?? 0})`}
          </button>
        ))}
      </div>

      {showBatchBar ? (
        <div className="billpipe-batch-bar" data-testid="billing-batch-bar">
          <span>{`${selectedItems.length} selected`}</span>
          <button
            type="button"
            className="btn btn-success"
            data-testid="billing-approve-selected"
            onClick={() => runBatch([...selected])}
            disabled={batchBusy}
          >
            {batchBusy ? 'Approving…' : 'Approve selected'}
          </button>
        </div>
      ) : null}

      {error && !state.forbidden ? (
        <p className="billpipe-error" role="alert" data-testid="billing-error">{error}</p>
      ) : null}

      {loading && groups.length === 0 ? (
        <p className="bkh-loading" data-testid="billing-loading">Loading…</p>
      ) : !error && groups.length === 0 ? (
        <p className="bkh-empty" data-testid="billing-empty">{viewEmptyState(view)}</p>
      ) : (
        <div className="billpipe-groups" data-testid="billing-groups">
          {groups.map((group, groupIndex) => {
            const selectable = view === 'need'
              && (group.items || []).some((item) => item.can?.approve);
            return (
              <section
                key={`${group.person}-${groupIndex}`}
                className="billpipe-group"
                aria-label={group.person}
                data-testid={`billing-group-${groupIndex}`}
              >
                <header className="billpipe-group-header">
                  <h3>{group.person}</h3>
                  <span className="billpipe-group-subtotal" data-testid="billing-group-subtotal">
                    {formatGroupSubtotal(group, { settled: view === 'billed' })}
                  </span>
                  {selectable ? (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      data-testid={`billing-group-approve-${groupIndex}`}
                      onClick={() => selectGroup(group)}
                      disabled={batchBusy}
                    >
                      Approve all
                    </button>
                  ) : null}
                </header>
                <div className="billpipe-rows">
                  {(group.items || []).map((item) => (
                    <ItemRow
                      key={item.id}
                      organisationId={organisationId}
                      item={item}
                      personName={group.person}
                      selectable={selectable}
                      selected={selected.has(item.id)}
                      onToggle={toggleRow}
                      onOpen={openDrawer}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {drawerItem ? (
        <BillingDrawer
          item={drawerItem}
          personName={drawerPerson}
          organisationId={organisationId}
          onClose={() => setDrawerItem(null)}
          refetchItem={refetchDrawerItem}
        />
      ) : null}

      <StepUpDialog
        challenge={stepUpChallenge}
        title="Verify billing decision"
        description="Confirm your identity before this billing action is applied."
        onComplete={() => {
          setStepUpChallenge(null);
          const replay = pendingReplayRef.current;
          pendingReplayRef.current = null;
          if (replay) replay();
        }}
        onCancel={() => {
          setStepUpChallenge(null);
          pendingReplayRef.current = null;
          setBatchErrors([{ id: '', message: 'Verification was cancelled — nothing was approved.', code: '' }]);
        }}
      />
    </div>
  );
};

export default BillingTab;
